import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useV1ProductionBomFamiliesList,
  useV1ProductionBomRevisionsList,
  v1ProductionBomFamiliesList,
} from "@/api/generated/v1/v1";
import { usePermission } from "@/auth/usePermission";
import { PageBanner } from "@/shared/components/PageBanner";
import { exportToExcel, exportToPDF } from "@/shared/utils/export";

export function BomFamilyListPage() {
  const navigate = useNavigate();
  const canCreate = usePermission("production.manage");

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilterTab, setActiveFilterTab] = useState<"all" | "mapped" | "unmapped">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch all BOM Families for reporting and pagination (page_size=200 to prevent truncation)
  const { data: rawFamiliesData, isLoading, isError, error } = useV1ProductionBomFamiliesList({
    page_size: 200,
  } as any);

  // Fetch published BOM Revisions for reporting KPI
  const { data: publishedRevisionsData, isLoading: revisionsLoading } = useV1ProductionBomRevisionsList({
    status: "published",
    page_size: 200,
  } as any);

  const allFamilies = useMemo(() => {
    return rawFamiliesData?.results || [];
  }, [rawFamiliesData]);

  // Compute KPI summary statistics
  const totalCount = rawFamiliesData?.count || allFamilies.length;

  const mappedCount = useMemo(() => {
    return allFamilies.filter(
      (f: any) => f.output_material_revision || f.output_material_revision_display
    ).length;
  }, [allFamilies]);

  const unmappedCount = useMemo(() => {
    return allFamilies.filter(
      (f: any) => !f.output_material_revision && !f.output_material_revision_display
    ).length;
  }, [allFamilies]);

  const publishedRevisionsCount = publishedRevisionsData?.count || publishedRevisionsData?.results?.length || 0;

  // Filtered families based on search term and active tab
  const filteredItems = useMemo(() => {
    return allFamilies.filter((item: any) => {
      // Tab filter
      const hasOutput = Boolean(item.output_material_revision || item.output_material_revision_display);
      if (activeFilterTab === "mapped" && !hasOutput) return false;
      if (activeFilterTab === "unmapped" && hasOutput) return false;

      // Search filter
      if (searchTerm.trim() !== "") {
        const query = searchTerm.toLowerCase().trim();
        const codeMatch = item.code?.toLowerCase().includes(query);
        const nameMatch = item.name?.toLowerCase().includes(query);
        const matName = (item.output_material_revision_display as any)?.name?.toLowerCase() || "";
        const matCode = (item.output_material_revision_display as any)?.code?.toLowerCase() || "";
        const matMatch = matName.includes(query) || matCode.includes(query);
        return codeMatch || nameMatch || matMatch;
      }

      return true;
    });
  }, [allFamilies, activeFilterTab, searchTerm]);

  // Paginated items
  const totalPages = Math.ceil(filteredItems.length / pageSize) || 1;
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, currentPage, pageSize]);

  // Export handlers
  const columns = [
    { header: "کد BOM", accessor: (row: any) => row.code },
    { header: "نام خانواده BOM", accessor: (row: any) => row.name },
    {
      header: "نام محصول خروجی",
      accessor: (row: any) => (row.output_material_revision_display as any)?.name || "—",
    },
  ];

  const fetchExportData = async () => {
    try {
      setIsExporting(true);
      const res = await v1ProductionBomFamiliesList({
        export: "true",
        ...(searchTerm && { search: searchTerm }),
      } as any);
      return (res as any).results || res;
    } catch (err) {
      alert("خطا در دریافت اطلاعات برای خروجی");
      throw err;
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    const rawData = await fetchExportData();
    await exportToExcel(rawData, columns, "bom_families_export");
  };

  const handleExportPDF = async () => {
    const rawData = await fetchExportData();
    await exportToPDF(rawData, columns, "bom_families_export");
  };

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      {/* 1. STANDARDIZED PAGE BANNER */}
      <PageBanner
        title="خانواده محصولات (BOM)"
        subtitle="تعریف و مدیریت کالبد اصلی ساختارهای درختی محصولات"
        icon="📦"
        helpLink="/help/bom/families"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          {canCreate && (
            <button
              onClick={() => navigate("/bom/families/create")}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: "800",
                fontSize: "0.9rem",
                border: "1px solid rgba(255, 255, 255, 0.8)",
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              <span>ایجاد خانواده BOM</span>
            </button>
          )}

          <button
            onClick={() => navigate("/bom")}
            style={{
              padding: "0.6rem 1.1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: "700",
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>←</span>
            <span>بازگشت به مدیریت تولید</span>
          </button>
        </div>
      </PageBanner>

      {/* 2. STATS REPORT GRID (TOP SUMMARY CARDS) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem",
        }}
      >
        {/* CARD 1: TOTAL BOM FAMILIES */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(0,114,112,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            📜
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              کل خانواده‌های BOM
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: "#007270" }}>
                {isLoading ? "..." : totalCount.toLocaleString("fa-IR")}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>ثبت شده</span>
            </div>
          </div>
        </div>

        {/* CARD 2: MAPPED OUTPUT MATERIALS */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(245,158,11,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            📦
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              دارای محصول خروجی
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: "#D4AF37" }}>
                {isLoading ? "..." : mappedCount.toLocaleString("fa-IR")}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>نگاشت کامل</span>
            </div>
          </div>
        </div>

        {/* CARD 3: UNMAPPED OUTPUT MATERIALS */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(239,68,68,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ⚠️
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              بدون محصول خروجی
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span
                style={{
                  fontSize: "1.3rem",
                  fontWeight: "800",
                  color: unmappedCount > 0 ? "#EF4444" : "#10B981",
                }}
              >
                {isLoading ? "..." : unmappedCount.toLocaleString("fa-IR")}
              </span>
              <span
                style={{
                  fontSize: "0.8rem",
                  fontWeight: "600",
                  color: unmappedCount > 0 ? "#EF4444" : "#10B981",
                }}
              >
                {unmappedCount > 0 ? "نیازمند بررسی" : "عالی"}
              </span>
            </div>
          </div>
        </div>

        {/* CARD 4: PUBLISHED REVISIONS */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(59,130,246,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ⚡
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              نسخه‌های منتشرشده (Published)
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: "#3B82F6" }}>
                {revisionsLoading ? "..." : publishedRevisionsCount.toLocaleString("fa-IR")}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#3B82F6" }}>آماده بهره‌برداری</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. CONTROL BAR & FILTER TABS (BRAND-CARD-EXTAND) */}
      <div
        className="brand-card-extand"
        style={{
          padding: "1rem 1.25rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        {/* FILTER TABS */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            onClick={() => {
              setActiveFilterTab("all");
              setCurrentPage(1);
            }}
            style={{
              padding: "0.55rem 1.1rem",
              borderRadius: "10px",
              fontSize: "0.88rem",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s ease",
              background: activeFilterTab === "all" ? "#007270" : "rgba(0, 114, 112, 0.08)",
              color: activeFilterTab === "all" ? "#FFFFFF" : "#007270",
              border: "1px solid rgba(0, 114, 112, 0.2)",
              boxShadow: activeFilterTab === "all" ? "0 4px 12px rgba(0, 114, 112, 0.2)" : "none",
            }}
          >
            📋 همه خانواده‌ها ({totalCount.toLocaleString("fa-IR")})
          </button>

          <button
            onClick={() => {
              setActiveFilterTab("mapped");
              setCurrentPage(1);
            }}
            style={{
              padding: "0.55rem 1.1rem",
              borderRadius: "10px",
              fontSize: "0.88rem",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s ease",
              background: activeFilterTab === "mapped" ? "#007270" : "rgba(0, 114, 112, 0.08)",
              color: activeFilterTab === "mapped" ? "#FFFFFF" : "#007270",
              border: "1px solid rgba(0, 114, 112, 0.2)",
              boxShadow: activeFilterTab === "mapped" ? "0 4px 12px rgba(0, 114, 112, 0.2)" : "none",
            }}
          >
            📦 دارای محصول خروجی ({mappedCount.toLocaleString("fa-IR")})
          </button>

          <button
            onClick={() => {
              setActiveFilterTab("unmapped");
              setCurrentPage(1);
            }}
            style={{
              padding: "0.55rem 1.1rem",
              borderRadius: "10px",
              fontSize: "0.88rem",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s ease",
              background: activeFilterTab === "unmapped" ? "#EF4444" : "rgba(239, 68, 68, 0.08)",
              color: activeFilterTab === "unmapped" ? "#FFFFFF" : "#EF4444",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              boxShadow: activeFilterTab === "unmapped" ? "0 4px 12px rgba(239, 68, 68, 0.2)" : "none",
            }}
          >
            ⚠️ بدون محصول خروجی ({unmappedCount.toLocaleString("fa-IR")})
          </button>
        </div>

        {/* ACTIONS & SEARCH */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          {/* EXPORT BUTTONS */}
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <button
              onClick={handleExportExcel}
              disabled={isExporting}
              style={{
                padding: "0.5rem 0.9rem",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "#FFFFFF",
                color: "var(--color-text-main, #2B2B2B)",
                fontSize: "0.83rem",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.35rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>📥</span>
              <span>خروجی Excel</span>
            </button>
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              style={{
                padding: "0.5rem 0.9rem",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "#FFFFFF",
                color: "var(--color-text-main, #2B2B2B)",
                fontSize: "0.83rem",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.35rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>📄</span>
              <span>خروجی PDF</span>
            </button>
            <button
              onClick={() => window.print()}
              style={{
                padding: "0.5rem 0.9rem",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "#FFFFFF",
                color: "var(--color-text-main, #2B2B2B)",
                fontSize: "0.83rem",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.35rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>🖨️</span>
              <span>چاپ</span>
            </button>
          </div>

          {/* SEARCH INPUT */}
          <div style={{ position: "relative", width: "260px" }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="جستجوی نام یا کد BOM..."
              style={{
                width: "100%",
                padding: "0.6rem 2.4rem 0.6rem 0.9rem",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "#FFFFFF",
                color: "var(--color-text-main, #2B2B2B)",
                fontSize: "0.88rem",
                fontWeight: "600",
                outline: "none",
                boxSizing: "border-box",
                transition: "all 0.2s ease",
              }}
            />
            <span
              style={{
                position: "absolute",
                right: "0.8rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "0.95rem",
                color: "#666",
              }}
            >
              🔍
            </span>
          </div>
        </div>
      </div>

      {/* 4. MAIN DATA TABLE CARD */}
      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        {isLoading && (
          <div style={{ padding: "3rem", textAlign: "center" }}>
            <span style={{ fontSize: "1.5rem" }}>⏳</span>
            <p style={{ color: "var(--color-text-muted, #666)", fontWeight: 700, marginTop: "0.5rem" }}>
              در حال بارگذاری خانواده‌های BOM...
            </p>
          </div>
        )}

        {isError && (
          <div
            style={{
              padding: "1rem",
              marginBottom: "1rem",
              background: "rgba(239, 68, 68, 0.1)",
              borderRight: "4px solid #EF4444",
              borderRadius: "8px",
            }}
          >
            <p style={{ color: "#EF4444", fontWeight: 700 }}>خطا در دریافت اطلاعات: {(error as Error)?.message}</p>
          </div>
        )}

        {!isLoading && !isError && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--color-border, #E4DCC8)", color: "#007270" }}>
                  <th style={{ padding: "0.9rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>کد BOM</th>
                  <th style={{ padding: "0.9rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>نام خانواده BOM</th>
                  <th style={{ padding: "0.9rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>
                    محصول خروجی (Output Material)
                  </th>
                  <th style={{ padding: "0.9rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>وضعیت نگاشت</th>
                  <th style={{ padding: "0.9rem 1rem", fontWeight: "800", fontSize: "0.9rem", textAlign: "left" }}>
                    عملیات
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                      <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📜</div>
                      <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                        هیچ خانواده BOM مطابق با جستجو یا فیلتر انتخابی یافت نشد.
                      </span>
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((item: any) => {
                    const outputMat = item.output_material_revision_display;
                    return (
                      <tr
                        key={item.id}
                        style={{
                          borderBottom: "1px solid var(--color-border, #E4DCC8)",
                          transition: "background 0.2s ease",
                        }}
                      >
                        <td style={{ padding: "0.9rem 1rem" }}>
                          <span
                            dir="ltr"
                            style={{
                              fontFamily: "monospace",
                              fontWeight: 800,
                              color: "#007270",
                              background: "rgba(0, 114, 112, 0.08)",
                              padding: "0.3rem 0.75rem",
                              borderRadius: "8px",
                              fontSize: "0.88rem",
                              display: "inline-block",
                            }}
                          >
                            {item.code}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "0.9rem 1rem",
                            fontWeight: 800,
                            color: "var(--color-text-main, #2B2B2B)",
                            fontSize: "0.95rem",
                          }}
                        >
                          {item.name}
                        </td>
                        <td style={{ padding: "0.9rem 1rem" }}>
                          {outputMat ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                              <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--color-text-main)" }}>
                                {outputMat.sku || outputMat.code ? `[${outputMat.sku || outputMat.code}] ` : ""}
                                {outputMat.name}
                              </span>
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 700,
                                  background: "rgba(245, 158, 11, 0.12)",
                                  color: "#D4AF37",
                                  padding: "0.15rem 0.5rem",
                                  borderRadius: "6px",
                                  border: "1px solid rgba(245, 158, 11, 0.2)",
                                }}
                              >
                                نسخه {outputMat.revision_no || "۱"}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: "#9CA3AF", fontSize: "0.85rem", fontWeight: 600 }}>
                              — بدون محصول خروجی —
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "0.9rem 1rem" }}>
                          {outputMat ? (
                            <span
                              style={{
                                padding: "0.3rem 0.75rem",
                                borderRadius: "12px",
                                background: "rgba(16, 185, 129, 0.12)",
                                color: "#10B981",
                                fontSize: "0.8rem",
                                fontWeight: 800,
                                border: "1px solid rgba(16, 185, 129, 0.2)",
                              }}
                            >
                              ✓ نگاشت شده
                            </span>
                          ) : (
                            <span
                              style={{
                                padding: "0.3rem 0.75rem",
                                borderRadius: "12px",
                                background: "rgba(239, 68, 68, 0.12)",
                                color: "#EF4444",
                                fontSize: "0.8rem",
                                fontWeight: 800,
                                border: "1px solid rgba(239, 68, 68, 0.2)",
                              }}
                            >
                              ⚠️ بدون محصول
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "0.9rem 1rem", textAlign: "left" }}>
                          <button
                            onClick={() => navigate(`/bom/families/${item.id}`)}
                            style={{
                              padding: "0.45rem 1rem",
                              borderRadius: "10px",
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              border: "1px solid #007270",
                              background: "transparent",
                              color: "#007270",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.4rem",
                            }}
                          >
                            <span>مشاهده و مدیریت</span>
                            <span>←</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 5. PAGINATION FOOTER */}
        {!isLoading && !isError && (
          <div
            style={{
              marginTop: "1.5rem",
              paddingTop: "1.25rem",
              borderTop: "1px solid var(--color-border, #E4DCC8)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            {/* ITEM COUNT & PAGE SIZE */}
            <div style={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
              <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted, #666)", fontWeight: 600 }}>
                نمایش <strong style={{ color: "#007270" }}>{paginatedItems.length.toLocaleString("fa-IR")}</strong> از{" "}
                <strong style={{ color: "#007270" }}>{filteredItems.length.toLocaleString("fa-IR")}</strong> خانواده BOM
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted, #666)", fontWeight: 600 }}>
                  تعداد در صفحه:
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "0.25rem 0.5rem",
                    borderRadius: "6px",
                    border: "1px solid var(--color-border, #E4DCC8)",
                    background: "#FFFFFF",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: "#2B2B2B",
                    outline: "none",
                  }}
                >
                  <option value={10}>۱۰</option>
                  <option value={25}>۲۵</option>
                  <option value={50}>۵۰</option>
                  <option value={100}>۱۰۰</option>
                </select>
              </div>
            </div>

            {/* PAGE CONTROLS */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "0.4rem 0.85rem",
                  borderRadius: "8px",
                  border: "1px solid var(--color-border, #E4DCC8)",
                  background: currentPage === 1 ? "#F3F4F6" : "#FFFFFF",
                  color: currentPage === 1 ? "#9CA3AF" : "#2B2B2B",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                قبلی
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: "0.4rem 0.85rem",
                    borderRadius: "8px",
                    border: page === currentPage ? "none" : "1px solid var(--color-border, #E4DCC8)",
                    background: page === currentPage ? "#007270" : "#FFFFFF",
                    color: page === currentPage ? "#FFFFFF" : "#2B2B2B",
                    fontSize: "0.85rem",
                    fontWeight: 800,
                    cursor: "pointer",
                    boxShadow: page === currentPage ? "0 2px 8px rgba(0, 114, 112, 0.25)" : "none",
                    transition: "all 0.2s ease",
                  }}
                >
                  {page.toLocaleString("fa-IR")}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{
                  padding: "0.4rem 0.85rem",
                  borderRadius: "8px",
                  border: "1px solid var(--color-border, #E4DCC8)",
                  background: currentPage === totalPages || totalPages === 0 ? "#F3F4F6" : "#FFFFFF",
                  color: currentPage === totalPages || totalPages === 0 ? "#9CA3AF" : "#2B2B2B",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  cursor: currentPage === totalPages || totalPages === 0 ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                بعدی
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
